import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import './InsertLinkModal.css'

type InsertLinkModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  onInsertLink: (url: string, linkText: string) => void
}

const InsertLinkModal: React.FC<InsertLinkModalProps> = ({
  isOpen,
  onRequestClose,
  onInsertLink,
}) => {
  const [url, setUrl] = useState('')
  const [linkText, setLinkText] = useState('')
  const urlInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && urlInputRef.current) {
      setTimeout(() => {
        urlInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleClose = (): void => {
    setUrl('')
    setLinkText('')
    onRequestClose()
  }

  const handleInsert = (): void => {
    if (url.trim()) {
      const text = linkText.trim() || url.trim()
      onInsertLink(url.trim(), text)
      handleClose()
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && url.trim()) {
      handleInsert()
    }
  }

  const isValidUrl = (): boolean => {
    if (!url.trim()) return false
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`)
      return true
    } catch {
      return false
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      className="insert-link-modal"
      backdrop="static"
      centered
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title>Insert link</Modal.Title>
      </Modal.Header>

      <Modal.Body className="insert-link-modal-body">
        <div className="insert-link-modal-content">
          <div className="insert-link-field">
            <label className="insert-link-label">URL</label>
            <input
              ref={urlInputRef}
              type="text"
              className="insert-link-input"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <div className="insert-link-hint">
              Enter the web address you want to link to
            </div>
          </div>

          <div className="insert-link-field">
            <label className="insert-link-label">Link text (optional)</label>
            <input
              type="text"
              className="insert-link-input"
              placeholder="Link text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <div className="insert-link-hint">
              The text that will be displayed as the link
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="ms-auto">
          <Button
            size="md"
            className="button-secondary me-2"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={handleInsert}
            disabled={!isValidUrl()}
          >
            Insert Link
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default InsertLinkModal
